import { createSignal, onCleanup, onMount } from 'solid-js';
import { Portal } from 'solid-js/web';

type ModalPreviewProps = {
  url: string;
  width?: string;
  height?: string;
  isMobile?: boolean;
  onClose?: () => void;
};

export const ModalPreview = (props: ModalPreviewProps) => {
  const {
    url,
    width = '500px',
    height = '600px',
    isMobile = false,
    onClose,
  } = props;
  const [isVisible, setIsVisible] = createSignal(true);
  const [isLoading, setIsLoading] = createSignal(true);

  let shadowRoot: ShadowRoot | null = null;

  const closeModal = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') closeModal();
  };

  const injectStylesToShadow = () => {
    if (shadowRoot) {
      const styleElement = document.createElement('style');
      styleElement.textContent = `
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `;
      shadowRoot.appendChild(styleElement);
    }
  };

  onMount(() => {
    injectStylesToShadow();
    document.addEventListener('keydown', handleKeyDown);
  });

  onCleanup(() => {
    document.removeEventListener('keydown', handleKeyDown);
  });

  return (
    <Portal useShadow ref={(el) => (shadowRoot = el?.shadowRoot)}>
      {/* Overlay */}
      {isVisible() && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            'background-color': 'rgba(0, 0, 0, 0.5)',
            'z-index': 9999999999,
            animation: 'fadeIn 0.3s ease-in-out',
          }}
          onClick={closeModal}
        />
      )}

      {/* Modal */}
      {isVisible() && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            'border-radius': isMobile ? '0' : '8px',
            'box-shadow':
              '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.06)',
            'z-index': 9999999999,
            animation: 'fadeIn 0.3s ease-in-out',
            width: isMobile ? '100%' : width,
            height: isMobile ? '100%' : height,
            'background-color': '#fff', // White background for loader
          }}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          {/* Close Button */}
          <button
            style={{
              position: 'absolute',
              top: isMobile ? '4px' : '-16px',
              right: isMobile ? '4px' : '-16px',
              width: '32px',
              height: '32px',
              border: '2px solid #6B7280',
              'border-radius': '50%',
              'background-color': '#fff',
              'font-size': '1.25rem',
              display: 'flex',
              'align-items': 'center',
              'justify-content': 'center',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease-in-out',
            }}
            onClick={closeModal}
          >
            ×
          </button>

          {/* Loader */}
          {isLoading() && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                'z-index': 1,
                display: 'flex',
                'align-items': 'center',
                'justify-content': 'center',
              }}
            >
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  border: '3px solid #e0e0e0',
                  'border-top': '3px solid #6B7280',
                  'border-radius': '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
            </div>
          )}

          {/* iFrame */}
          <iframe
            src={url}
            style={{
              width: isMobile ? '100%' : width,
              height: isMobile ? '100%' : height,
              border: 'none',
              visibility: isLoading() ? 'hidden' : 'visible',
            }}
            onLoad={() => setIsLoading(false)}
          />
        </div>
      )}
    </Portal>
  );
};
